/// <amd-module name="Build/Designer/YamlTab" />
import { BuildDefinition } from "Build/Client/Contracts/Build";
import { EditorConstants } from "Build/Designer/Constants";
import { YamlEditor } from "Build/Designer/YamlEditor";
import { focusYamlAssistantButton, YamlEditorHeader } from "Build/Designer/YamlEditorHeader";
import { YamlSidePanel } from "Build/Designer/YamlSidePanel";
import * as React from "react";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";




interface YamlTabProps {

    /* Subheader props */
    repositoryName: string,
    repositoryIconName: string,
    repositoryUrl: string,
    branchName: string,
    fileName: string,
    fileUrl: string,
    supportFPS: boolean
    isYamlDirty: boolean,
    definition?: BuildDefinition
    onBranchSelected: (branchName: string) => void;
    onBranchPickerError?: (errorMessage: string) => void;
    onFileNameChanged?: (fileName: string) => void;

    /* Editor props */
    content: string;
    editor: monaco.editor.ICodeEditor;
    spinnerMessage?: string;
    onContentUpdated: (newContent: string) => void;
    onEditorCreated: (editor: monaco.editor.IEditor) => void;
    onEditorDisposing?: (editor: monaco.editor.IEditor) => void;
    onViewTemplate?: (template: string) => void;

    /* Side panel props */
    sidePanelInitiallyOpen: boolean;
}

interface YamlTabState {
    sidePanelIsOpen: boolean;
}

/**
 * YamlTab component includes the YAML editor, subheader, and task assistant pane.
 */
export class YamlTab extends VssComponent<YamlTabProps, YamlTabState, IVssComponentContext> {

    constructor(props: YamlTabProps, context: IVssComponentContext) {
        super(props, context);
        this.state = { sidePanelIsOpen: props.sidePanelInitiallyOpen };
    }

    public render() {
        const props = this.props;
        return <div className="ci-yaml-editor-tab flex-grow flex-row">
            <div className={"flex-row absolute-fill"}>
                <div className="flex-column flex-grow relative">
                    <div className="ci-yaml-header-container relative">
                        <YamlEditorHeader
                            branchName={props.branchName}
                            definition={props.definition}
                            fileName={props.fileName}
                            fileUrl={props.fileUrl}
                            repositoryIconName={props.repositoryIconName}
                            repositoryName={props.repositoryName}
                            repositoryUrl={props.repositoryUrl}
                            supportFPS={props.supportFPS}
                            sidePanelIsOpen={this.state.sidePanelIsOpen}
                            isYamlDirty={props.isYamlDirty}
                            onBranchSelected={props.onBranchSelected}
                            onBranchPickerError={props.onBranchPickerError}
                            onSidePanelButtonClick={this._onSidePanelButtonClick}
                            onFileNameChanged={this.props.onFileNameChanged}
                        />
                    </div>
                    <div className="relative flex-grow">
                        <div className="absolute-fill">
                            <YamlEditor
                                content={props.content}
                                spinnerMessage={props.spinnerMessage}
                                errorMessage=""
                                isReadOnly={false}
                                elementToFocusOnEscape={"." + EditorConstants.ElementToFocusOnEscape}
                                onEditorContentUpdated={this._onEditorContentUpdated}
                                onEditorCreated={props.onEditorCreated}
                                onEditorDisposing={props.onEditorDisposing}
                                sidePanelIsOpen={this.state.sidePanelIsOpen}
                                codeLensOptions={{onEditTask: this._openSidePanel}}
                            />
                        </div>
                    </div>
                </div>
                <YamlSidePanel
                    editor={props.editor}
                    isOpen={this.state.sidePanelIsOpen}
                    onDismiss={this._onSidePanelButtonClick} />
            </div>
        </div>
    }

    private _onEditorContentUpdated = (editor: any /*IEditor*/) => {
        if (editor && this.props.onContentUpdated) {
            const content = editor.getValue() || "";
            this.props.onContentUpdated(content);
        }
    }

    private _onSidePanelButtonClick = () => {
        this.setState(prevState => ({
            sidePanelIsOpen: !prevState.sidePanelIsOpen
        }), () => {
            // Since the YAML Assistant toggle button moves between the sub-header (Show) and panel (Hide),
            // we shift the focus to the newly visible element on toggle to provide the illusion of consistent keyboard focus.
            focusYamlAssistantButton();
        });
    }

    private _openSidePanel = () => {
        if (!this.state.sidePanelIsOpen) {
            this.setState({
                sidePanelIsOpen: true
            });
        }
    }
}
